import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Calendar, Clock, MapPin, Users, Share2, BarChart3, Settings, Building } from "lucide-react";
import { Event } from "@/types/event";
import { eventService } from "@/services/eventService";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { GuestManagement } from "@/components/GuestManagement";
import { EventImageGenerator } from "@/components/EventImageGenerator";
import NavigationBar from "@/components/NavigationBar";
import { useSubscription } from "@/hooks/useSubscription";
import { EventClientAssignment } from "@/components/EventClientAssignment";
import { ApprovalRequestManager } from "@/components/ApprovalRequestManager";

export default function EventDetailOwner() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { subscription, hasClientPortalAccess } = useSubscription();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      loadEventData();
    }
  }, [eventId]);

  const loadEventData = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      console.log('🔍 [EventDetailOwner] Loading event data for eventId:', eventId);
      
      const eventData = await eventService.getEventById(eventId);
      console.log('🔍 [EventDetailOwner] Loaded event data:', eventData);
      
      if (!eventData) {
        console.log('🔍 [EventDetailOwner] Event not found');
        toast({
          title: "Event not found", 
          description: "The event you're looking for doesn't exist or you don't have permission to view it.",
          variant: "destructive",
        });
        navigate('/dashboard');
        return;
      }
      
      // Ensure the event has all required properties for old events
      const normalizedEvent = {
        ...eventData,
        options: eventData.options || [],
        guests: eventData.guests || [],
        votes: eventData.votes || [],
        description: eventData.description || "",
        totalVoters: eventData.totalVoters || 0,
      };
      
      console.log('🔍 [EventDetailOwner] Normalized event data:', normalizedEvent);
      setEvent(normalizedEvent);
    } catch (error) {
      console.error('❌ [EventDetailOwner] Error loading event:', error);
      toast({
        title: "Error",
        description: "Failed to load event details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShareEvent = () => {
    if (!event) return;
    
    const shareUrl = `${window.location.origin}/vote/${event.shareableLink}`;
    navigator.clipboard.writeText(shareUrl);
    toast({
      title: "Link copied!",
      description: "The shareable link has been copied to your clipboard.",
    });
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getEventStatusBadge = () => {
    if (!event) return null;
    
    const now = new Date();
    const deadline = event.deadline ? new Date(event.deadline) : null;
    
    if (!event.isActive) {
      return <Badge variant="secondary">Closed</Badge>;
    } else if (deadline && deadline < now) {
      return <Badge variant="destructive">Expired</Badge>;
    } else {
      return <Badge variant="default">Active</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-semibold">Event not found</h2>
            <p className="text-muted-foreground">The event you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-2 ml-auto">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShareEvent}
              className="gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share Event
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(`/analytics?event=${event.id}`)}
              className="gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </Button>
          </div>
        </div>

        {/* Event Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{event.name}</h1>
              {getEventStatusBadge()}
            </div>
            <p className="text-muted-foreground">Hosted by {event.hostName}</p>
          </div>
        </div>

        {/* Event Overview Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              <CardTitle>Event Overview</CardTitle>
            </div>
            <CardDescription>
              {event.description || "Casual coffee meeting with location and time options"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{event.options.filter(opt => opt.type === 'date').length} date options</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{event.options.filter(opt => opt.type === 'time').length} time options</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{event.options.filter(opt => opt.type === 'location').length} location options</p>
                </div>
              </div>
            </div>
            
            {event.deadline && (
              <p className="text-sm text-muted-foreground mb-6">
                Voting deadline: {formatDate(event.deadline)}
              </p>
            )}
            
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleShareEvent}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share Event
              </Button>
              
              <EventImageGenerator event={event} />
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate(`/analytics?event=${event.id}`)}
                className="gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                View Analytics
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Client Management Section - Only for pro/enterprise users */}
        {hasClientPortalAccess() && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building className="w-5 h-5" />
                <CardTitle>Client Management</CardTitle>
              </div>
              <CardDescription>
                Assign this event to a client and manage approvals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <EventClientAssignment 
                eventId={event.id}
                eventTitle={event.name}
                currentClientIds={event.clientId ? [event.clientId] : []}
                onAssignmentUpdate={loadEventData}
              />
              
              {event.clientId && (
                <ApprovalRequestManager
                  eventId={event.id}
                  eventTitle={event.name}
                  clientId={event.clientId}
                  onUpdate={loadEventData}
                />
              )}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="guests" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="guests" className="gap-2">
              <Users className="w-4 h-4" />
              Guest Management
            </TabsTrigger>
            <TabsTrigger value="results" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Voting Results
            </TabsTrigger>
          </TabsList>

          <TabsContent value="guests" className="space-y-6">
            <GuestManagement 
              eventId={event.id} 
              guests={(event.guests || []).map(guest => ({ 
                ...guest, 
                status: (guest.hasVoted || false) ? 'responded' : 'pending' as const 
              }))} 
              onGuestsUpdate={loadEventData} 
            />
          </TabsContent>

          <TabsContent value="results" className="space-y-6">
            <div className="grid gap-4">
              {event.options.map((option) => {
                const votes = event.votes.filter(vote => vote.optionId === option.id);
                const preferVotes = votes.filter(vote => vote.voteType === 'prefer').length;
                const totalVotes = event.totalVoters || 1;
                const percentage = Math.round((preferVotes / totalVotes) * 100);

                return (
                  <Card key={option.id}>
                    <CardContent className="py-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {option.type === 'date' && <Calendar className="w-4 h-4 text-muted-foreground" />}
                            {option.type === 'time' && <Clock className="w-4 h-4 text-muted-foreground" />}
                            {option.type === 'location' && <MapPin className="w-4 h-4 text-muted-foreground" />}
                            <span className="font-medium">
                              {option.type === 'date' ? formatDate(option.value) : option.value}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {preferVotes} of {totalVotes} prefer
                          </div>
                        </div>
                        <Progress value={percentage} className="h-2" />
                        <div className="text-right text-sm text-muted-foreground">
                          {percentage}%
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {event.options.length === 0 && (
                <Card>
                  <CardContent className="py-8">
                    <div className="text-center text-muted-foreground">
                      No voting options configured for this event.
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}